import * as Sentry from "@sentry/react";

/**
 * Initialize Sentry for error tracking and monitoring
 */
export function initSentry() {
  // Don't initialize Sentry in development
  if (import.meta.env.DEV) {
    console.log("Sentry will not be enabled in development mode.");
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
  const release = import.meta.env.VITE_SENTRY_RELEASE;

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn("Sentry DSN not configured. Set VITE_SENTRY_DSN to enable error tracking.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    enableLogs: true,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // No masking - capture everything
        maskAllText: false,
        maskAllInputs: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring (production only)
    tracesSampleRate: 0.1,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0, // Always record replays on errors
    
    // Set sample rate for profiling - this is relative to tracesSampleRate
    profilesSampleRate: 0.1,
  });
}

/**
 * Set user context for Sentry
 */
export function setSentryUser(user: { id?: string; email?: string; username?: string }) {
  // Only set user in production (when Sentry is initialized)
  if (!import.meta.env.DEV) {
    Sentry.setUser(user);
  }
}

/**
 * Clear user context (e.g., on logout)
 */
export function clearSentryUser() {
  // Only clear user in production (when Sentry is initialized)
  if (!import.meta.env.DEV) {
    Sentry.setUser(null);
  }
}

